import React, { useState } from "react";
import { FiChevronDown, FiExternalLink } from "react-icons/fi";
import { useInfoProvider } from "../../context/InfoContext";

const FAQs = () => {
  const { info } = useInfoProvider();
  const [activeIndex, setActiveIndex] = useState(null);

  const toggleAccordion = (index) => {
    setActiveIndex(activeIndex === index ? null : index);
  };

  const faqData = [
    {
      question: "What is Gigsam?",
      answer:
        "Gigsam is a trusted hiring platform that connects individuals and businesses with skilled professionals for various services.",
    },
    {
      question: "What services does Gigsam offer?",
      answer:
        "We provide a platform for hiring professionals across various domains. For detailed services, visit our ",
      link: {
        text: "services page",
        url: "https://gigsam.com/service",
      },
    },
    {
      question: "How can I book a service through Gigsam? ",
      answer: "To book a service, navigate to Gigsam's services page ",
      link: {
        text: "services page",
        url: "https://gigsam.com/service",
      },
      additionalText:
        " and follow the instructions provided for the specific service you require.",
    },
    {
      question: "Are the professionals on Gigsam verified?",
      answer:
        "Gigsam emphasizes connecting clients with trusted professionals. Professionals are verified by virtual interview processes, it's advisable to have your own verification process in place.",
    },
    {
      question: "Is Gigsam available in my city?",
      answer:
        "Gigsam's website does not list specific service areas. To determine availability in your location, please reach out to us directly through contact information provided on the website.",
    },
    {
      question: "How do I become a service provider on Gigsam?",
      answer:
        "If you're interested in offering your services through Gigsam, visit their ",
      link: {
        text: "service page",
        url: "https://gigsam.com/service",
      },
      additionalText:
        "for potential opportunities or contact them directly for more information on joining their network.",
    },
    {
      question: "What payment methods are accepted?",
      answer:
        "Digital payment methods are accepted. For detailed information regarding payments, please contact Gigsam directly through their official channels.",
    },
    {
      question: "How can I contact Gigsam for support?",
      answer:
        "For support or any inquiries, visit Gigsam's official website at www.gigsam.com and use the contact information provided to reach out to our team",
      contactOptions: [
        { method: "Email", value: info?.app_email },
        { method: "Phone", value: info?.app_contact },
      ],
    },
  ];

  return (
    <div className="w-full flex items-center justify-center flex-col px-4 sm:px-6 lg:px-8 py-12 bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-4xl w-full">
        <div className="text-center mb-12">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-3">
            Frequently Asked Questions
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Find answers to common questions about Gigsam services and platform
          </p>
        </div>

        <div className="space-y-4 w-full">
          {faqData.map((item, index) => (
            <div
              key={index}
              className={`w-full transition-all duration-200 ${
                activeIndex === index ? "mb-6" : ""
              }`}
            >
              <div
                className={`bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow duration-200 border border-gray-200 ${
                  activeIndex === index
                    ? "ring-2 ring-blue-500 border-blue-500"
                    : ""
                }`}
              >
                <button
                  onClick={() => toggleAccordion(index)}
                  className="w-full text-left flex justify-between items-center group"
                  aria-expanded={activeIndex === index}
                >
                  <span className="text-lg font-semibold text-gray-900 group-hover:text-blue-600 transition-colors">
                    {item.question}
                  </span>
                  <div
                    className={`ml-4 transition-transform duration-200 ${
                      activeIndex === index
                        ? "rotate-180 text-blue-600"
                        : "text-gray-400"
                    }`}
                  >
                    <FiChevronDown size={24} />
                  </div>
                </button>

                <div
                  className={`grid transition-all duration-300 ease-in-out overflow-hidden ${
                    activeIndex === index
                      ? "grid-rows-[1fr] opacity-100 mt-4"
                      : "grid-rows-[0fr] opacity-0"
                  }`}
                >
                  <div className="overflow-hidden">
                    <div className="text-gray-700 leading-relaxed">
                      {item.answer}

                      {item.link && (
                        <a
                          href={item.link.url}
                          //   target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-800 inline-flex items-center ml-1"
                        >
                          {item.link.text}
                          <FiExternalLink className="ml-1" size={14} />
                        </a>
                      )}
                      {item.additionalText}

                      {item.highlight && (
                        <div className="mt-3 px-4 py-3 bg-blue-50 rounded-lg border border-blue-100 text-blue-800">
                          {item.highlight}
                        </div>
                      )}

                      {item.tag && (
                        <span className="inline-block mt-3 px-3 py-1 text-xs font-semibold bg-blue-100 text-blue-800 rounded-full">
                          {item.tag}
                        </span>
                      )}

                      {item.paymentMethods && (
                        <div className="mt-3 flex flex-wrap gap-2">
                          {item.paymentMethods.map((method, i) => (
                            <span
                              key={i}
                              className="px-3 py-1 bg-gray-100 rounded-full text-sm"
                            >
                              {method}
                            </span>
                          ))}
                        </div>
                      )}

                      {item.contactOptions && (
                        <ul className="mt-3 space-y-2">
                          {item.contactOptions.map((option, i) => (
                            <li key={i} className="flex">
                              <span className="font-medium text-gray-900 w-24">
                                {option.method}:
                              </span>
                              <span>{option.value}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* <div className="mt-12 text-center">
          <p className="text-gray-600 mb-4">
            Didn't find what you're looking for?
          </p>
          <a
            href="https://gigsam.com/contact"
            className="inline-flex items-center px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors shadow-sm"
          >
            Contact Our Support Team
            <svg
              className="ml-2 w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M14 5l7 7m0 0l-7 7m7-7H3"
              />
            </svg>
          </a>
        </div> */}
      </div>
    </div>
  );
};

export default FAQs;
